import { lazy, Suspense } from "react";
import React from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { LinearProgress, CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "react-query";
import { ReactQueryDevtools } from "react-query/devtools";
import { PersistGate } from "redux-persist/integration/react";

import ErrorBoundary from "./providers/ErrorBoundary/ErrorBoundary";
import { LayoutProvider } from "./context/LayoutContext";
import { StaticDataProvider } from "./context/staticDataContext";
import { CachedDataProvider } from "./context/cachedDataContext";
import { UserProvider } from "./context/UserContext";
import I18nProvider from "./providers/I18n/I18n";
import Layout from "./components/Layout";
import Login from "./pages/login";
import { FT_ENABLE_CONFIRM_API } from "./providers/FeatureToggle/FeatureToggle";
import CampaignOptOut from "./pages/campaignOptOut/CampaignOptOut";
import Themes from "./themes";
import store, { persistor } from "./utils/store";
import "./index.css";

const root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

const App = lazy(() => {
  return import.meta.env.VITE_MODE === "admin"
    ? import("./components/App.admin")
    : import("./components/App");
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const Activate = lazy(() => import("./pages/login/activate/Activate"));
const EnterpriseSignup = lazy(
  () => import("./pages/enterpriseSignup/EnterpriseSignup"),
);

const dashBoardPath = FT_ENABLE_CONFIRM_API ? "/app/home" : "/app/dashboard";
const appRoutes = {
  admin: [
    {
      path: "/",
      redirect: dashBoardPath,
    },
    {
      path: "/public",
      redirect: dashBoardPath,
    },
    {
      path: "/app/*",
      Component: Layout,
      isPrivate: true,
    },
    {
      path: "/login",
      Component: Login,
      isPublic: true,
    },
    {
      path: "/opt-out",
      Component: CampaignOptOut,
      isPublic: true,
      allowLogin: true,
    },
    {
      path: "/signup",
      Component: Login,
      isPublic: true,
    },
    {
      path: "/forgotPassword",
      Component: Login,
      isPublic: true,
    },
    {
      path: "/activate",
      Component: Activate,
      isPublic: true,
    },
    {
      path: "/reset",
      Component: Activate,
      isPublic: true,
    },
    {
      path: "/enterprise_signup",
      Component: EnterpriseSignup,
      isPrivate: true,
    },
  ],
  user: [
    {
      path: "/",
      redirect: dashBoardPath,
    },
    {
      path: "/public",
      redirect: dashBoardPath,
    },
    {
      path: "/opt-out",
      Component: CampaignOptOut,
      isPublic: true,
    },
    {
      path: "/app/*",
      Component: Layout,
      isPrivate: true,
    },
    {
      path: "/login",
      Component: Login,
      isPublic: true,
    },
    {
      path: "/forgotPassword",
      Component: Login,
      isPublic: true,
    },
    {
      path: "/activate",
      Component: Activate,
      isPublic: true,
    },
  ],
};

const theme = Themes.default;

root.render(
  <I18nProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <QueryClientProvider client={queryClient} contextSharing={true}>
          <LayoutProvider>
            <UserProvider>
              <StaticDataProvider>
                <CachedDataProvider>
                  <Suspense fallback={<LinearProgress variant="query" />}>
                    <Provider store={store}>
                      <PersistGate loading={null} persistor={persistor}>
                        <App
                          routes={
                            appRoutes[
                              import.meta.env.VITE_MODE === "admin"
                                ? "admin"
                                : "user"
                            ]
                          }
                        />
                      </PersistGate>
                    </Provider>
                  </Suspense>
                </CachedDataProvider>
              </StaticDataProvider>
            </UserProvider>
          </LayoutProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </I18nProvider>,
);
